import * as api from '@gitbook/api-client';

import { compareSiteTypes } from './table_sites';

export type MeterPrice = {
    /**
     * Labels for the meter unit.
     * Short labels are used in cost summaries, while the long ones are used in charts and other
     * more descriptive locations.
     */
    unitLabels: {
        singular: {
            short: string;
            long: string;
        };
        plural: {
            short: string;
            long: string;
        };
    };

    /**
     * Flat initial prices in USD
     */
    flatInitialPrice: {
        [api.BillingInterval.Monthly]: number;
        [api.BillingInterval.Yearly]: number;
    };

    /**
     * The number of units at which the price per unit starts.
     */
    startingAt: number;

    /**
     * The number of units per batch.
     */
    unitsPerBatch: number;

    /**
     * The price per unit in USD.
     */
    pricePerBatch: number;

    /**
     * Description for the addon related to the meter.
     */
    addon?: {
        title: string;
        description: string;
        sellingPoints: string[];
    };
};

export const METER_PRICES: Record<api.BillingMeter, MeterPrice> = {
    [api.BillingMeter.AiAgentResponses]: {
        unitLabels: {
            singular: {
                short: 'response',
                long: 'Agent Response',
            },
            plural: {
                short: 'responses',
                long: 'Agent Responses',
            },
        },
        startingAt: 0,
        unitsPerBatch: 1,
        pricePerBatch: 0,
        flatInitialPrice: {
            [api.BillingInterval.Monthly]: 0,
            [api.BillingInterval.Yearly]: 0,
        },
    },
    [api.BillingMeter.AiResponses]: {
        unitLabels: {
            singular: {
                short: 'question',
                long: 'Answered Question',
            },
            plural: {
                short: 'questions',
                long: 'Answered Questions',
            },
        },
        // Soft cap that won't affect the org in any way, we may get in touch if they go above it.
        startingAt: 500,
        unitsPerBatch: 1,
        pricePerBatch: 0,
        flatInitialPrice: {
            [api.BillingInterval.Monthly]: 0,
            [api.BillingInterval.Yearly]: 0,
        },
    },
    [api.BillingMeter.TranslationWords]: {
        unitLabels: {
            singular: {
                short: 'word',
                long: 'Translated word',
            },
            plural: {
                short: 'words',
                long: 'Translated words',
            },
        },
        startingAt: 50000,
        unitsPerBatch: 1000,
        pricePerBatch: 0.2,
        flatInitialPrice: {
            [api.BillingInterval.Monthly]: 25,
            [api.BillingInterval.Yearly]: 250,
        },
        addon: {
            title: 'Translations',
            description:
                'Generate AI translations of your content that automatically update whenever you make changes.',
            sellingPoints: [
                'Instantly reach new markets and boost your SEO.',
                `Maintain your brand voice across ${Object.values(api.TranslationLanguage).length} languages with AI-driven translations`,
                'Every content update is automatically reflected in all translations',
                'Save time and costs by eliminating manual translations',
                'Customize translations using a glossary or custom instructions',
            ],
        },
    },
};

/**
 * A map of meters to the minimum site type required to enable them.
 * If a meter has no requirements, the value can be null.
 */
export const METER_TO_SITE_TYPE: Record<api.BillingMeter, api.SiteType | null> = {
    [api.BillingMeter.TranslationWords]: null,
    [api.BillingMeter.AiResponses]: api.SiteType.Ultimate,
    [api.BillingMeter.AiAgentResponses]: api.SiteType.Ultimate,
};

/**
 * A small helper to determine which site plan is required for the given site to
 * be able to use the given meter.
 */
export function getRequiredPlanForSiteToUseMeter(args: {
    meterName: api.BillingMeter;
    site: api.Site;
}): api.SiteType | undefined {
    const { meterName, site } = args;
    const requiredSiteType = METER_TO_SITE_TYPE[meterName];

    if (!requiredSiteType) {
        return undefined;
    }

    const currentSiteType = site.appliedType ?? site.type;
    if (compareSiteTypes(requiredSiteType, currentSiteType) <= 0) {
        return undefined;
    }

    return requiredSiteType;
}

/**
 * Mapping from billable add-on meter to the pricing feature gate used by upgrade flows.
 */
export const ADDON_TO_PRICING_FEATURE_KEY: Record<
    api.BillingMeter,
    api.OrganizationFeature.Translations | api.OrganizationFeature.AiLens
> = {
    [api.BillingMeter.TranslationWords]: api.OrganizationFeature.Translations,
    [api.BillingMeter.AiResponses]: api.OrganizationFeature.AiLens,
    [api.BillingMeter.AiAgentResponses]: api.OrganizationFeature.AiLens,
};
